/**
 * Subscription Service
 * Handles communication with CARBOwaste API for subscription management
 */

import { config } from '../config/environment';
import { CarboWasteEndpoints_Subscription, CarboWasteEndpoints_HealthCheck } from '../api/endpoints';
import { DeviceSubscription, ApiErrorResponse } from './DeviceTypes';

/**
 * Check if API is available using health check endpoint
 * GET /api/health
 * 
 * @returns boolean - true if API is available, false otherwise
 */
export const checkHealthEndpoint = async (): Promise<boolean> => {
  const url = CarboWasteEndpoints_HealthCheck;

  console.log('📡 Checking API health:', url);

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout for health check

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    console.log('📡 Health check response:', {
      status: response.status,
      ok: response.ok,
    });

    // Health endpoint should return 200
    if (response.status === 200) {
      console.log('✅ API is healthy');
      return true;
    }

    console.warn('⚠️ API health check failed with status:', response.status);
    return false;

  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        console.error('⏱️ Health check timeout');
      } else {
        console.error('🌐 Health check network error:', error.message);
      }
    }
    return false;
  }
};

/**
 * Create Trial Subscription for unregistered device
 */
const createTrialSubscription = (fingerprint: string): DeviceSubscription => {
  const now = new Date();

  return {
    subscriptionId: `trial-${fingerprint.substring(0, 16)}`,
    source: "Portal",
    planId: 'carbowaste_bdo_trial',
    status: 'Trial',
    autoRenew: false,
    termUnit: 'Unlimited',
    purchaserEmail: 'trial@carbowaste.com',
    startDate: now.toISOString(),
    endDate: '9999-12-31T23:59:59Z', // Unlimited
    companies: [],
    limits: {
      companiesLimit: 1,
      eupsLimit: 9000,
    },
  };
};

/**
 * Fetch device subscription from API
 * GET /api/v1/Subscription/{fingerprint}
 * 
 * @param fingerprint - Device fingerprint
 * @returns DeviceSubscription (registered or Trial)
 * @throws Error only on API failures (not 404)
 */
export const fetchDeviceSubscription = async (
  fingerprint: string
): Promise<DeviceSubscription> => {
  const url = `${CarboWasteEndpoints_Subscription}/${fingerprint}`;

  console.log('📡 Fetching device subscription:', { fingerprint, url });
  console.log('🔗 Fetch URL:', url);
  console.log('🔧 Config baseUrl:', config.api.baseUrl);
  console.log('🔧 Endpoint base:', CarboWasteEndpoints_Subscription);

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.api.timeout);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    console.log('📡 API Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    // Success - device is registered
    if (response.status === 200) {
      const data: DeviceSubscription = await response.json();
      console.log('✅ Subscription fetched successfully:', {
        subscriptionId: data.subscriptionId,
        status: data.status,
        companiesCount: data.companies?.length || 0,
      });
      console.log('📦 Full API Response Data:', JSON.stringify(data, null, 2));
      console.log('🏢 Companies structure:', data.companies?.map(c => ({
        companyId: c.companyId,
        name: c.name,
        eupsCount: c.eups?.length || 0,
        eupsPresent: !!c.eups,
        firstEupId: c.eups?.[0]?.eupId
      })));
      console.log('✅ fetchDeviceSubscription completed (200 OK - registered device)');
      return data;
    }

    // Not Found - device not registered, create Trial subscription
    if (response.status === 404) {
      console.log('📋 Device not registered (404) - creating TRIAL subscription');
      const trialSubscription = createTrialSubscription(fingerprint);
      console.log('✨ TRIAL subscription created:', {
        subscriptionId: trialSubscription.subscriptionId,
        status: trialSubscription.status,
      });
      console.log('✅ fetchDeviceSubscription completed (404 - Trial mode)');
      return trialSubscription;
    }

    // Handle other error statuses
    const errorData: ApiErrorResponse = await response.json().catch(() => ({
      errorCode: `CW${response.status}`,
      message: response.statusText || 'Unknown error',
    }));

    console.error('❌ API Error:', {
      status: response.status,
      errorCode: errorData.errorCode,
      message: errorData.message,
    });

    throw new Error(errorData.message || `API Error: ${response.status}`);

  } catch (error) {
    if (error instanceof Error) {
      // AbortError - timeout
      if (error.name === 'AbortError') {
        console.error('⏱️ Request timeout');
        throw new Error('api_error_timeout_local');
      }

      // Network error
      if (error.message.includes('fetch')) {
        console.error('🌐 Network error');
        throw new Error('api_error_network');
      }

      // Re-throw other errors
      throw error;
    }

    // Unknown error
    console.error('❌ Unknown error:', error);
    throw new Error('api_error_unknown');
  }
};

/**
 * Check if subscription is active (including Trial)
 */
export const isSubscriptionActive = (subscription: DeviceSubscription | null): boolean => {
  if (!subscription) return false;

  // Trial is always active
  if (subscription.status === 'Trial') return true;

  const now = new Date();
  const endDate = new Date(subscription.endDate);

  return (
    subscription.status === 'Subscribed' &&
    endDate >= now
  );
};

/**
 * Get days remaining in subscription
 */
export const getSubscriptionDaysRemaining = (subscription: DeviceSubscription | null): number => {
  if (!subscription) return 0;

  const now = new Date();
  const endDate = new Date(subscription.endDate);
  const diffTime = endDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays > 0 ? diffDays : 0;
};

/**
 * Get all active EUPs from subscription
 */
export const getActiveEups = (subscription: DeviceSubscription | null) => {
  if (!subscription) return [];

  return subscription.companies.flatMap(company =>
    company.eups.filter(eup => eup.isActive)
  );
};

/**
 * Get company by ID
 */
export const getCompanyById = (
  subscription: DeviceSubscription | null,
  companyId: string
) => {
  if (!subscription) return null;
  return subscription.companies.find(c => c.companyId === companyId);
};
